/**
 * utils/platformTokens.ts
 * ──────────────────────────────────────────────────────────────────────────
 * Exact numeric design tokens ported 1:1 from PLATFROM_DESIGN_INSTRUCTIONS/
 * design/theme.css so the platform (non-auth) screens are a carbon copy of
 * SK Platform.html. Colors come from the theme (theme/colors.ts already maps
 * the OKLCH tokens to hex); these constants cover the px-level scales the CSS
 * defines as custom properties.
 *
 * Use alongside useTheme().colors — never hardcode a color or a spacing value
 * in a screen; pull from here so the whole app stays consistent.
 */

/** Font sizes — --fs-xs … --fs-3xl */
export const FS = {
  xs: 11,
  sm: 12,
  md: 13,
  lg: 15,
  xl: 18,
  xxl: 22,
  xxxl: 28,
} as const;

/** Font weights — --fw-* */
export const FW = {
  regular: '400',
  medium: '500',
  semibold: '600',
  bold: '700',
} as const;

/** Spacing scale (4px base) — --sp-* */
export const SP = {
  s0: 0,
  s1: 4,
  s2: 8,
  s3: 12,
  s4: 16,
  s5: 20,
  s6: 24,
  s8: 32,
  s10: 40,
  s12: 48,
  s16: 64,
} as const;

/** Radii — --r-* */
export const R = {
  r1: 3,
  r2: 5,
  r3: 8,
  r4: 12,
  r5: 16,
  full: 999,
} as const;

/** The single responsive breakpoint the design uses (SubNav → select). */
export const NARROW_BP = 900;

/** status → chip tone, ported from data.jsx STATUS_TONE. */
export type Tone = 'success' | 'warn' | 'danger' | 'info' | 'neutral' | 'accent';

export const STATUS_TONE: Record<string, Tone> = {
  active: 'success', verified: 'success', paid: 'success', ready: 'success',
  complete: 'success', completed: 'success', published: 'success',
  pending: 'warn', trial: 'warn', processing: 'warn', queued: 'warn',
  'near-limit': 'warn', beta: 'warn', draft: 'warn', past_due: 'warn',
  voicemail: 'info', info: 'info',
  deactivated: 'danger', failed: 'danger', canceled: 'danger', cancelled: 'danger',
  'over-limit': 'danger', deprecated: 'danger', suspended: 'danger', missed: 'danger',
};

export function statusTone(status: string): Tone {
  return STATUS_TONE[status] || 'neutral';
}

/** Indian Rupee formatter — ported from data.jsx inr(). */
export function inr(n: number): string {
  return '₹' + n.toLocaleString('en-IN', { maximumFractionDigits: 2 });
}
